import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReplayIcon from "@mui/icons-material/Replay";
import { Grid, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MDButton from "components/MDButton";
import moment from "moment";
import { useState } from "react";
import TooltipLight from "utils/TooltipLight";

const ArrowNegative = () => (
  <MDButton
    sx={{
      border: "1px solid red",
      borderRadius: "50%",
      minWidth: "1rem",
      minHeight: "1rem",
      width: "1rem",
      height: "1rem",
      p: "1rem",
      cursor: "default",
    }}
    disableRipple
  >
    <ExpandMoreIcon sx={{ color: "red", p: 0 }} />
  </MDButton>
);

const ArrowPositive = () => {
  const theme = useTheme();

  return (
    <MDButton
      sx={{
        border: "1px solid",
        borderRadius: "50%",
        minWidth: "1rem",
        minHeight: "1rem",
        width: "1rem",
        height: "1rem",
        p: "1rem",
        cursor: "default",
        color: theme.palette.trendGreen.main,
      }}
      disableRipple
    >
      <ExpandLessIcon sx={{ color: theme.palette.trendGreen.main, p: 0 }} />
    </MDButton>
  );
};

const LedgerRow = ({
  row,
  selectedTab,
  handleRestoreRevenue,
  handleDeleteRevenue,
  handleEditRevenue,
  loginUserReportingHierarchy,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [dealName, setDealName] = useState(row.deal_name);
  const [revenue, setRevenue] = useState(row.is_positive ? row.revenue : -1 * row.revenue);
  // Users can only edit or delete revenue that are from people who report to them
  const userRevenueRowIsInReportHierarchy = loginUserReportingHierarchy.includes(
    row?.UserCompaniesLink?.FK_users_id
  );
  // Button display logic
  const deletable = row.FK_revenue_sources_source === "manual" && !row.deletedAt;
  const editable = row.FK_revenue_sources_source === "manual" && !row.deletedAt;
  const restorable = row.FK_revenue_sources_source === "manual" && row.deletedAt;

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    setIsEditMode(false);
  };

  const handleSaveClick = (revenueId, newDealName, newRevenue) => {
    handleEditRevenue(revenueId, newDealName, newRevenue);
    setIsEditMode(false);
  };

  const formatNumber = (number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);

  const getRevenueColor = () => {
    const theme = useTheme();
    if (row.deletedAt) return "#bdbdbd";
    return row.is_positive ? theme.palette.trendGreen.main : "red";
  };

  return (
    <Grid
      container
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ justifyContent: "space-between", py: "0.4rem", alignItems: "flex-start" }}
    >
      <Grid item>
        <Grid container alignItems="center">
          <Grid item pr={2}>
            {row.is_positive ? <ArrowPositive /> : <ArrowNegative />}
          </Grid>
          <Grid item>
            <Grid container direction="column">
              <Grid item display="flex" alignItems="center">
                {isEditMode ? (
                  <TextField
                    defaultValue={dealName}
                    placeholder="Deal Name"
                    onChange={(event) => setDealName(event.target.value)}
                    size="small"
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleSaveClick(row.id, dealName, revenue);
                      }
                    }}
                  />
                ) : (
                  <Typography
                    variant="h6"
                    sx={{
                      textDecoration: row.deletedAt ? "line-through" : null,
                      color: row.deletedAt || !row.deal_name ? "#bdbdbd" : "inherit",
                      fontStyle: row.deal_name ? "normal" : "italic",
                    }}
                  >
                    {row.deal_name || "Deal Name"}
                  </Typography>
                )}

                {selectedTab === 1 && (
                  <Typography fontSize="0.8rem" fontWeight="400" pl={0.85}>
                    {row.UserCompaniesLink.name}
                  </Typography>
                )}
              </Grid>
              <Grid item>
                <Typography sx={{ color: "#666", fontWeight: "400", fontSize: "0.9rem" }}>
                  {row.deletedAt
                    ? moment(row.deletedAt).format("[Deleted at] dddd MMMM D[, at] h:mm A")
                    : moment(row.revenueRecognitionDate || row.createdAt).format(
                        "dddd MMMM D[, at] h:mm A"
                      )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container>
          <Grid item>
            {isEditMode ? (
              <TextField
                defaultValue={revenue}
                placeholder="Revenue"
                onChange={(event) => setRevenue(event.target.value)}
                size="small"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSaveClick(row.id, dealName, revenue);
                  }
                }}
              />
            ) : (
              <Typography
                sx={{
                  color: getRevenueColor(),
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  textDecoration: row.deletedAt ? "line-through" : null,
                }}
              >
                {`${row.is_positive ? "+" : "-"} ${formatNumber(row.revenue)}`}
              </Typography>
            )}
          </Grid>
          <Grid item sx={{ ml: "0.5rem", width: "40px" }}>
            {/* Edit Button */}
            {isHovered && editable && !isEditMode && userRevenueRowIsInReportHierarchy && (
              <TooltipLight title="Edit the details of this deal" arrow placement="top">
                <EditIcon onClick={() => handleEditClick()} sx={{ cursor: "pointer" }} />
              </TooltipLight>
            )}

            {/* Delete Button */}
            {isHovered && deletable && !isEditMode && userRevenueRowIsInReportHierarchy && (
              <TooltipLight title="Delete this deal (undoable)" arrow placement="top">
                <DeleteOutlineIcon
                  onClick={() => handleDeleteRevenue(row.id)}
                  sx={{ cursor: "pointer", color: "red" }}
                />
              </TooltipLight>
            )}

            {/* Save Button */}
            {editable && isEditMode && (
              <TooltipLight title="Save edits" arrow placement="top">
                <CheckIcon
                  onClick={() => handleSaveClick(row.id, dealName, revenue)}
                  sx={{ cursor: "pointer", color: "green", mt: "8px" }}
                />
              </TooltipLight>
            )}

            {/* Cancel Button */}
            {editable && isEditMode && (
              <TooltipLight title="Cancel edits" arrow placement="top">
                <CloseIcon onClick={() => handleCancelClick()} sx={{ cursor: "pointer" }} />
              </TooltipLight>
            )}

            {/* Restore Button */}
            {restorable && (
              <TooltipLight title="Restore this deal" arrow placement="top">
                <ReplayIcon
                  onClick={() => handleRestoreRevenue(row.id)}
                  sx={{ cursor: "pointer", width: "1.4rem", height: "1.4rem", mt: "4px" }}
                />
              </TooltipLight>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LedgerRow;
